import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { InsightsService } from 'shared-states';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class RoutePerformanceService {
  private pendingNavigations = new Map<number, number>();
  private readonly enableLogging = environment.appInsights.enableDebug;

  constructor(
    private router: Router,
    private insightsService: InsightsService
  ) {}

  /**
   * Called during APP_INITIALIZER to begin listening to router events.
   */
  init(): void {
    this.log('🚀 RoutePerformanceService initialized — listening to router events');

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.onNavigationStart(event);
      } else if (event instanceof NavigationEnd) {
        this.onNavigationEnd(event);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.onNavigationCancelled(event.id);
      }
    });
  }

  private onNavigationStart(event: NavigationStart): void {
    const timestamp = typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
    this.pendingNavigations.set(event.id, timestamp);
    this.log(`⏱️ [NavigationStart] id=${event.id}, url=${event.url}`);
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const startTimestamp = this.pendingNavigations.get(event.id);
    if (startTimestamp == null) {
      this.log(`⚠️ [NavigationEnd] id=${event.id} — no matching NavigationStart found, skipping`);
      return;
    }

    const endTimestamp = typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
    const durationMs = endTimestamp - startTimestamp;

    const properties = {
      route: event.urlAfterRedirects,
      routeName: this.normalizeRouteName(event.urlAfterRedirects),
      durationMs: String(durationMs),
      navigationId: String(event.id)
    };

    this.insightsService.trackEvent('RouteLoadTime', properties);

    this.log(`✅ [RouteLoadTime] trackEvent sent`, properties);

    this.pendingNavigations.delete(event.id);
  }

  private onNavigationCancelled(navigationId: number): void {
    this.pendingNavigations.delete(navigationId);
    this.log(`❌ [NavigationCancel/Error] id=${navigationId} — measurement discarded`);
  }

  private log(message: string, data?: any): void {
    if (this.enableLogging) {
      if (data) {
        console.log(`[RoutePerformanceService] ${message}`, data);
      } else {
        console.log(`[RoutePerformanceService] ${message}`);
      }
    }
  }

  /**
   * Normalizes a URL path into a route name.
   * Strips leading slash, replaces `/` with `-`, removes query params and fragments.
   * Example: '/orders/basket?page=1#top' → 'orders-basket'
   */
  normalizeRouteName(url: string): string {
    // Remove query params and fragments
    let path = url.split('?')[0].split('#')[0];
    // Strip leading slash
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    // Replace remaining slashes with dashes
    return path.replace(/\//g, '-');
  }
}
